import { ModuleReducer } from '@leverege/api-redux';
const moduleSort = (a, b) => {
  const an = a.module.displayName || a.module.name;
  const bn = b.module.displayName || b.module.name;
  const v = an.localeCompare(bn);
  return v === 0 ? a.module.project === 'imagine' ? 1 : -1 : v;
};
const permissionSort = (a, b) => {
  return a.permission.name.localeCompare(b.permission.name);
};
function sortRoles(roles) {
  roles.sort((a, b) => {
    const as = a.metadata?.priority == null ? 1000 : a.metadata.priority;
    const bs = b.metadata?.priority == null ? 1000 : b.metadata.priority;
    if (as === bs) {
      const an = (a.displayName || a.name).toLowerCase();
      const bn = (b.displayName || b.name).toLowerCase();
      if (an === bn) {
        return 0;
      }
      return an < bn ? -1 : 1;
    }
    return as - bs;
  });
  return roles;
}
function calcCustomRoles(roles) {
  const custom = roles.filter(r => {
    return r.scope === 'custom';
  });
  return sortRoles(custom);
}
function calcProjectRoles(roles, projectId) {
  const prj = roles.filter(r => {
    return r.projectId === projectId && r.scope !== 'custom';
  });
  return sortRoles(prj);
}

/**
 * returns an array of { module, permissions : [ { permission, index } ] }
 */
function filterAndGroupRoles(prjRoles, imgRoles, filter, roleFilter) {
  const {
    inCustom,
    inProject,
    inImagine,
    search: oSearch
  } = filter;
  let roles = inImagine ? [...imgRoles.filter(r => {
    if (roleFilter) {
      return roleFilter(r);
    }
    return true;
  })] : [];
  if (inProject || inCustom) {
    roles = [...prjRoles.filter(r => {
      if (roleFilter && !roleFilter(r)) {
        return false;
      }
      if (r.scope === 'custom') {
        return inCustom;
      }
      return inProject;
    }), ...roles];
  }
  let search = oSearch == null ? null : oSearch.trim();
  if (search?.length == 0) {
    // eslint-disable-line eqeqeq
    search = null;
  }
  if (search) {
    const terms = search.toLowerCase().split(' ').filter(t => t.length > 0);
    roles = roles.filter(r => {
      const name = (r.displayName || r.name).toLowerCase();
      return terms.every(t => name.indexOf(t) >= 0);
    });
  }
  return sortRoles(roles);
}

/**
 * Takes [ permissions ] and turns it into { <}
 */
function groupPermissions(permissions = [], modules, asArray) {
  const g = {};
  permissions.forEach((p, index) => {
    const key = `${p.projectId}:${p.module}`;
    if (g[key] == null) {
      const pmods = ModuleReducer.getModules(modules, p.projectId) || [];
      const m = pmods.find(m => {
        return m.name === p.module;
      });
      g[key] = {
        module: m || {
          name: p.module
        },
        permissions: [{
          permission: p,
          index
        }]
      };
    } else {
      g[key].permissions.push({
        permission: p,
        index
      });
    }
  });

  // sort arrays
  Object.values(g).forEach(grp => {
    grp.permissions.sort(permissionSort);
  });
  if (asArray) {
    // sort modules
    return Object.values(g).sort(moduleSort);
  }
  return g;
}

/**
 * returns an array of { module, permissions : [ { permission, index } ] }
 */
function filterAndGroupPermissions(modules, prjPerms, imgPerms, filter) {
  const {
    inCustom,
    inProject,
    inImagine,
    search: oSearch
  } = filter;
  let grps = {};
  let search = oSearch == null ? null : oSearch.trim();
  if (search?.length == 0) {
    // eslint-disable-line eqeqeq
    search = null;
  }
  if (inImagine) {
    grps = groupPermissions(imgPerms, modules);
  }
  if (inCustom || inProject) {
    grps = {
      ...grps,
      ...groupPermissions(prjPerms.filter(p => {
        const isCustom = p.scope === 'custom';
        return inCustom && isCustom || inProject && !isCustom;
      }), modules)
    };
  }
  let arr = Object.values(grps);
  if (search) {
    const terms = search.toLowerCase().split(' ').filter(t => t.length > 0);
    arr = arr.filter(g => {
      const name = (g.module.displayName || g.module.name).toLowerCase();
      const unfound = terms.filter(t => name.indexOf(t) < 0);
      if (unfound.length === 0) {
        return true; // found all terms
      }
      // check if permissions has the remaining
      g.permissions = g.permissions.filter(p => {
        const name = (p.permission.displayName || p.permission.name).toLowerCase();
        return unfound.every(t => name.indexOf(t) >= 0);
      });
      return g.permissions.length > 0;
    });
  }
  return arr.sort(moduleSort);
}

// Augments filterAndGroupPermissions result with any modules without permissions
function filterModules(grps, modules, projectId, filter) {
  const {
    inCustom,
    inProject,
    search: oSearch
  } = filter;
  let search = oSearch == null ? null : oSearch.trim();
  if (search?.length == 0) {
    // eslint-disable-line eqeqeq
    search = null;
  }
  const has = grps.reduce((acc, g) => {
    acc[`${g.module.projectId}:${g.module.name}`] = g;
    return acc;
  }, {});
  const m = ModuleReducer.getModules(modules, projectId) || [];
  const nGrps = [...grps];
  m.forEach(mdl => {
    const key = `${projectId}:${mdl.name}`;
    if (has[key] != null) {
      return;
    }
    if (mdl.scope === 'custom') {
      if (inCustom === false) {
        return;
      }
    } else if (inProject === false) {
      return;
    }
    if (search) {
      const terms = search.toLowerCase().split(' ').filter(t => t.length > 0);
      const name = (mdl.displayName || mdl.name).toLowerCase();
      if (!terms.every(t => name.indexOf(t) >= 0)) {
        return;
      }
    }
    nGrps.push({
      module: mdl,
      permissions: []
    });
  });
  return nGrps.sort(moduleSort);
}
const idReducer = (acc, cv) => {
  acc[cv.id] = cv;
  return acc;
};
function diffPermissionLists(oPerms, nPerms) {
  const rtn = {
    add: [],
    remove: []
  };
  const oObj = oPerms.reduce(idReducer, {});
  const nObj = nPerms.reduce(idReducer, {});
  oPerms.forEach(p => {
    if (nObj[p.id] == null) {
      rtn.remove.push(p);
    }
  });
  nPerms.forEach(p => {
    if (oObj[p.id] == null) {
      rtn.add.push(p);
    }
  });
  return rtn;
}

/**
 * Creates a function that returns true if the supplied role is in the array
 * of roles given to this function.
 */
function createUserRoleFilter(roles) {
  if (roles == null || !Array.isArray(roles) || roles.length === 0) {
    return null;
  }
  return role => {
    for (let n = 0; n < roles.length; n++) {
      if (roles[n].id === role.id) {
        return true;
      }
    }
    return false;
  };
}
export { sortRoles, calcCustomRoles, calcProjectRoles, calcCustomRoles as calcCustomPermissions, calcProjectRoles as calcProjectPermissions, filterAndGroupRoles, filterAndGroupPermissions, groupPermissions, filterModules, diffPermissionLists, createUserRoleFilter };